import React, { useEffect, useState } from 'react';
import { mockUsers } from '../utils/mockData';
import { FileCheck, ChevronRight, Battery, SunMedium, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Collect all projects from all users
const getAllProjects = () => {
  const allProjects = mockUsers.flatMap(user => user.projects.map(project => ({
    ...project,
    userId: user.id,
    userName: user.name
  })));
  return allProjects;
};
// Types for sorting
type SortField = 'id' | 'userName' | 'address' | 'financeOption' | 'financeStatus' | 'projectStatus';
type SortDirection = 'asc' | 'desc';
export const Proposals = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('all');
  const allProjects = getAllProjects();
  // Sorting states
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [sortedProjects, setSortedProjects] = useState<any[]>([]);
  // Filter projects based on active tab
  useEffect(() => {
    const filtered = activeTab === 'all' ? allProjects : allProjects.filter(project => project.projectStatus === activeTab);
    setSortedProjects(filtered);
  }, [activeTab, allProjects]);
  // Sort projects when sort parameters or filtered projects change
  useEffect(() => {
    const sorted = [...sortedProjects].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'id':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'userName':
          comparison = a.userName.localeCompare(b.userName);
          break;
        case 'address':
          comparison = a.address.localeCompare(b.address);
          break;
        case 'financeOption':
          comparison = a.financeOption.localeCompare(b.financeOption);
          break;
        case 'financeStatus':
          comparison = a.financeStatus.localeCompare(b.financeStatus);
          break;
        case 'projectStatus':
          comparison = a.projectStatus.localeCompare(b.projectStatus);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    setSortedProjects(sorted);
  }, [sortField, sortDirection]);
  // Handle header click for sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" data-id="element-423" /> : <ChevronDown className="h-4 w-4 ml-1" data-id="element-424" />;
  };
  // Define the order of status tabs
  const statusOrder = ['all', 'pending', 'accepted', 'scheduled', 'commissioned'];
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'commissioned':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getFinanceStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pre-approved':
        return 'bg-amber-100 text-amber-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'finance accepted':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="w-full" data-id="element-425">
      <div className="p-6" data-id="element-426">
        <div className="max-w-6xl mx-auto" data-id="element-427">
          <div className="flex justify-between items-center mb-6" data-id="element-428">
            <h1 className="text-2xl font-bold" data-id="element-429">Proposals Management</h1>
            <span className="text-sm text-muted-foreground" data-id="element-430">
              {sortedProjects.length} proposals found
            </span>
          </div>
          {/* Status Tabs */}
          <div className="border-b mb-6" data-id="element-431">
            <div className="flex overflow-x-auto" data-id="element-432">
              {statusOrder.map(status => <button key={status} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === status ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} onClick={() => setActiveTab(status)} data-id="element-433">
                  {status === 'all' ? 'All Proposals' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>)}
            </div>
          </div>
          {/* Proposals Table */}
          <div className="w-full overflow-auto rounded-md border" data-id="element-434">
            <table className="w-full text-sm" data-id="element-435">
              <thead className="bg-muted/50" data-id="element-436">
                <tr data-id="element-437">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('id')} data-id="element-438">
                    <div className="flex items-center" data-id="element-439">
                      Proposal #{renderSortIndicator('id')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('userName')} data-id="element-440">
                    <div className="flex items-center" data-id="element-441">
                      Customer
                      {renderSortIndicator('userName')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('address')} data-id="element-442">
                    <div className="flex items-center" data-id="element-443">
                      Location
                      {renderSortIndicator('address')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('financeOption')} data-id="element-444">
                    <div className="flex items-center" data-id="element-445">
                      Financing
                      {renderSortIndicator('financeOption')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('projectStatus')} data-id="element-446">
                    <div className="flex items-center" data-id="element-447">
                      Status
                      {renderSortIndicator('projectStatus')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground" data-id="element-448">
                    System Overview
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground" data-id="element-449">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-450">
                {sortedProjects.map(project => <tr key={`${project.userId}-${project.id}`} className="border-t transition-colors hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/users/${project.userId}/proposals/${project.id}`, {
                state: {
                  from: 'proposals'
                }
              })} data-id="element-451">
                    <td className="px-4 py-3 font-medium" data-id="element-452">
                      <div className="flex items-center" data-id="element-453">
                        <FileCheck className="h-4 w-4 mr-2 text-primary" data-id="element-454" />
                        SQL-{project.id}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" data-id="element-455">
                      {project.userName}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" data-id="element-456">
                      {project.address}
                    </td>
                    <td className="px-4 py-3" data-id="element-457">
                      <div className="flex flex-col" data-id="element-458">
                        <span className="text-sm font-medium capitalize mb-1" data-id="element-459">
                          {project.financeOption}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFinanceStatusBadgeClass(project.financeStatus)}`} data-id="element-460">
                          {project.financeStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-461">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(project.projectStatus)}`} data-id="element-462">
                        {project.projectStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-463">
                      <div className="flex flex-col gap-2" data-id="element-464">
                        <div className="flex items-center" data-id="element-465">
                          <SunMedium className="h-3 w-3 mr-1 text-blue-600" data-id="element-466" />
                          <span className="text-xs text-muted-foreground" data-id="element-467">
                            33 panels (20.5kW)
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-468">
                          <Battery className="h-3 w-3 mr-1 text-purple-600" data-id="element-469" />
                          <span className="text-xs text-muted-foreground" data-id="element-470">
                            21.48kWh storage
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-471">
                      <div className="text-primary flex items-center text-sm" data-id="element-472">
                        View <ChevronRight className="h-4 w-4 ml-1" data-id="element-473" />
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {sortedProjects.length === 0 && <div className="text-center py-8 border rounded-lg bg-muted/20 mt-4" data-id="element-474">
              <p className="text-muted-foreground" data-id="element-475">
                No proposals found for this status.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};